// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
// Floating Quick Notepad Widget - visible for trainers & admins only
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { quicknotesAPI } from "@/lib/api";
import { toast } from "sonner";
import {
  StickyNote, Plus, Trash2, ChevronDown, ChevronUp, X, Check,
  GripVertical, BookOpen, ListTodo, AlertTriangle, Layers, Minimize2
} from "lucide-react";
import "./FloatingNotepad.css";

const CATEGORIES = [
  { value: "general", label: "Général", icon: StickyNote, color: "#7c3aed" },
  { value: "progress", label: "Progression", icon: BookOpen, color: "#059669" },
  { value: "todo", label: "À faire", icon: ListTodo, color: "#2563eb" },
  { value: "issue", label: "Problème", icon: AlertTriangle, color: "#dc2626" },
];

export default function FloatingNotepad() {
  const { workspaceId } = useParams();
  const { user, isAdmin } = useAuth();
  const userId = user?.id;
  const role = user?.role;

  // Only for trainers and admins
  const canAccess = role === "admin" || role === "trainer";

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newContent, setNewContent] = useState("");
  const [newCategory, setNewCategory] = useState("general");
  const [newColor, setNewColor] = useState("#7c3aed");
  const [filterCategory, setFilterCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: window.innerHeight - 500 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });

  const loadNotes = useCallback(async () => {
    if (!workspaceId || !userId) return;
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      const res = await quicknotesAPI.list(workspaceId, userId, params);
      setNotes(res.data);
    } catch (err) {
      console.error("Failed to load quicknotes:", err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, userId, filterCategory]);

  useEffect(() => {
    if (isOpen && canAccess) loadNotes();
  }, [isOpen, canAccess, loadNotes]);

  const addNote = async () => {
    if (!newContent.trim()) return;
    try {
      const res = await quicknotesAPI.create(workspaceId, userId, {
        content: newContent.trim(),
        category: newCategory,
        color: newColor,
      });
      setNotes((prev) => [res.data, ...prev]);
      setNewContent("");
      toast.success("Note ajoutée");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const toggleDone = async (note) => {
    try {
      await quicknotesAPI.update(note.id, userId, { is_done: !note.is_done });
      setNotes((prev) =>
        prev.map((n) => (n.id === note.id ? { ...n, is_done: !n.is_done } : n))
      );
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await quicknotesAPI.delete(noteId, userId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      toast.error("Erreur");
    }
  };

  // Drag handlers
  const onMouseDown = (e) => {
    if (e.target.closest(".notepad-no-drag")) return;
    setDragging(true);
    offsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging) return;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 320, e.clientX - offsetRef.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 60, e.clientY - offsetRef.current.y)),
      });
    };
    const onMouseUp = () => setDragging(false);
    if (dragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);

  if (!canAccess) return null;

  // Floating button when closed
  if (!isOpen) {
    return (
      <button
        className="floating-notepad-trigger"
        onClick={() => setIsOpen(true)}
        title="Bloc-notes de progression"
      >
        <StickyNote size={20} />
      </button>
    );
  }

  const catInfo = (cat) => CATEGORIES.find((c) => c.value === cat) || CATEGORIES[0];

  return (
    <div
      className={`floating-notepad ${isMinimized ? "minimized" : ""}`}
      style={{ left: position.x, top: position.y }}
      ref={dragRef}
    >
      {/* Header - draggable */}
      <div className="notepad-header" onMouseDown={onMouseDown}>
        <div className="notepad-header-left">
          <GripVertical size={14} className="notepad-grip" />
          <StickyNote size={14} />
          <span>Bloc-notes</span>
        </div>
        <div className="notepad-header-actions notepad-no-drag">
          <button onClick={() => setIsMinimized(!isMinimized)} title={isMinimized ? "Agrandir" : "Réduire"}>
            {isMinimized ? <ChevronUp size={14} /> : <Minimize2 size={14} />}
          </button>
          <button onClick={() => setIsOpen(false)} title="Fermer">
            <X size={14} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Category filter */}
          <div className="notepad-filters notepad-no-drag">
            <button
              className={`notepad-filter-btn ${filterCategory === null ? "active" : ""}`}
              onClick={() => setFilterCategory(null)}
            >
              <Layers size={12} /> Tout
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                className={`notepad-filter-btn ${filterCategory === cat.value ? "active" : ""}`}
                onClick={() => setFilterCategory(cat.value)}
                style={{ "--cat-color": cat.color }}
              >
                <cat.icon size={12} /> {cat.label}
              </button>
            ))}
          </div>

          {/* Add new note */}
          <div className="notepad-add notepad-no-drag">
            <div className="notepad-add-row">
              <textarea
                placeholder="Ajouter une note..."
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                rows={5}
              />
            </div>
            <div className="notepad-color-picker">
              {["#7c3aed","#2563eb","#059669","#d97706","#dc2626","#db2777","#6366f1","#0891b2","#65a30d","#78716c"].map((c) => (
                <button
                  key={c}
                  className={`notepad-color-dot ${newColor === c ? "active" : ""}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setNewColor(c)}
                />
              ))}
            </div>
            <div className="notepad-add-actions">
              <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <button className="notepad-add-btn" onClick={addNote} disabled={!newContent.trim()}>
                <Plus size={14} /> Ajouter
              </button>
            </div>
          </div>

          {/* Notes list */}
          <div className="notepad-list notepad-no-drag">
            {notes.length === 0 && (
              <div className="notepad-empty">
                Aucune note. Commencez par noter la progression du cours.
              </div>
            )}
            {notes.map((note) => {
              const cat = catInfo(note.category);
              return (
                <div
                  key={note.id}
                  className={`notepad-item ${note.is_done ? "done" : ""}`}
                  style={{ 
                    borderLeftColor: note.color || cat.color,
                    backgroundColor: `${note.color || cat.color}15`
                  }}
                >
                  <div className="notepad-item-top">
                    <button className="notepad-check" onClick={() => toggleDone(note)}>
                      {note.is_done ? <Check size={12} /> : <div className="notepad-check-empty" />}
                    </button>
                    <span className="notepad-item-text">{note.content}</span>
                    <button className="notepad-item-delete" onClick={() => deleteNote(note.id)}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <div className="notepad-item-meta">
                    <span className="notepad-item-cat" style={{ color: cat.color }}>
                      <cat.icon size={10} /> {cat.label}
                    </span>
                    <span className="notepad-item-date">
                      {new Date(note.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
                      })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
